import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { isAdmin, isAuthor, signInWithPassword, signOut, useIsAuthenticated } from '@/core/auth';
import { trackLogin, trackLogout } from '@/core/tracking';
import type { SignupValues } from '../domain/signup';
import type { CurrentUser } from '../domain/user';
import { identityApi } from '../infrastructure/api';

export const identityKeys = {
  all: ['identity'] as const,
  me: () => [...identityKeys.all, 'me'] as const,
};

export const meQuery = queryOptions({
  queryKey: identityKeys.me(),
  queryFn: identityApi.me,
  // O perfil muda raramente; a API já o mantém em cache pelo tempo do token.
  staleTime: 5 * 60_000,
});

export function useMe() {
  return useQuery(meQuery);
}

/**
 * Usuário autenticado. Só dentro da área logada: o `SessionGate` garante
 * que o perfil já está em cache quando qualquer tela é renderizada.
 */
export function useCurrentUser(): CurrentUser {
  return useSuspenseQuery(meQuery).data;
}

/** O que a interface pode mostrar. Quem autoriza de verdade é a API. */
export function usePermissions() {
  const { roles } = useCurrentUser();
  return { roles, canAuthor: isAuthor(roles), isAdmin: isAdmin(roles) };
}

/**
 * Para telas públicas (eventos, vitrine de aulas, produtos): o usuário se
 * houver sessão, senão `null` — sem disparar login nem `/identity/me`.
 */
export function useOptionalPermissions() {
  const signedIn = useIsAuthenticated();
  const me = useQuery({ ...meQuery, enabled: signedIn });
  const user = signedIn ? (me.data ?? null) : null;
  const roles = user?.roles ?? [];
  return { user, signedIn, canAuthor: isAuthor(roles), isAdmin: isAdmin(roles) };
}

export function useSignIn() {
  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      signInWithPassword(username.trim(), password),
    // Atividade: a entrada, com o método. Login social e SSO são detectados
    // pela sessão nova (ver core/tracking).
    onSuccess: () => trackLogin('password'),
  });
}

/**
 * A conta foi criada, mas o login automático logo depois falhou. A pessoa
 * não pode ver "erro no cadastro" (tentar de novo daria "e-mail já
 * cadastrado"): a tela manda para o login com o e-mail preenchido.
 */
export class SignedUpButNotSignedIn extends Error {
  readonly email: string;

  constructor(email: string) {
    super('Conta criada; o login automático falhou.');
    this.name = 'SignedUpButNotSignedIn';
    this.email = email;
  }
}

/**
 * "Criar conta": cadastra pela API e entra em seguida com a mesma senha.
 * Ao entrar, o token chega ao AuthProvider e a rota /login leva a pessoa
 * para onde ela ia — o mesmo caminho do formulário de login.
 */
export function useSignUp() {
  return useMutation({
    mutationFn: async ({ name, email, password }: SignupValues) => {
      const cleanEmail = email.trim();
      await identityApi.register({ name: name.trim().replace(/\s+/g, ' '), email: cleanEmail, password });
      try {
        await signInWithPassword(cleanEmail, password);
      } catch {
        throw new SignedUpButNotSignedIn(cleanEmail);
      }
    },
    onSuccess: () => trackLogin('signup'),
  });
}

/**
 * Sai e volta ao login. O cache é limpo pela rota /login (ver routes/login.tsx).
 * A saída vai para a atividade antes do `signOut`, enquanto ainda há token.
 */
export function useLogout() {
  return () => {
    trackLogout();
    return signOut();
  };
}
